import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { UserCircle, Trash, Plus } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { super_admin: "Super Admin", admin: "Admin", staff: "Staff", viewer: "Viewer" };
const ROLE_COLORS = { super_admin: "bg-purple-100 text-purple-800", admin: "bg-red-100 text-red-800", staff: "bg-blue-100 text-blue-800", viewer: "bg-slate-100 text-slate-600" };

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [inviteError, setInviteError] = useState("");

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { const res = await axios.get(`${API}/users`); setUsers(res.data); }
    catch (err) { console.error("Error:", err); }
    finally { setLoading(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try { await axios.put(`${API}/users/${userId}/role`, { role: newRole }); fetchUsers(); }
    catch (err) { alert(err.response?.data?.detail || "Failed to update role"); }
  };

  const handlePremiumToggle = async (userId, enabled) => {
    try { await axios.put(`${API}/users/${userId}/premium`, { enabled }); fetchUsers(); }
    catch (err) { alert(err.response?.data?.detail || "Failed to update premium access"); }
  };

  const handleTransportToggle = async (userId, enabled) => {
    try { await axios.put(`${API}/users/${userId}/transport-invoice`, { enabled }); fetchUsers(); }
    catch (err) { alert(err.response?.data?.detail || "Failed to update transport-invoice access"); }
  };

  const isSuperAdmin = currentUser?.role === "super_admin";

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try { await axios.delete(`${API}/users/${userId}`); fetchUsers(); }
    catch (err) { alert(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError("");
    try {
      await axios.post(`${API}/users/invite`, inviteForm);
      setInviteOpen(false);
      setInviteForm({ name: "", email: "", password: "", role: "staff" });
      fetchUsers();
    } catch (err) {
      setInviteError(err.response?.data?.detail || "Failed to create user");
    }
  };

  return (
    <div className="p-4 sm:p-8" data-testid="user-management-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">User Management</h1>
          <p className="text-slate-500 text-sm">Manage team access and roles</p>
        </div>
        <button onClick={() => setInviteOpen(true)} data-testid="invite-user-btn" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2">
          <Plus size={20} weight="bold" /> Add User
        </button>
      </div>

      {/* Role Legend */}
      <div className="mb-6 flex flex-wrap gap-4">
        {["admin", "staff", "viewer"].map((role) => (
          <div key={role} className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider ${ROLE_COLORS[role]}`}>{ROLE_LABELS[role]}</span>
            <span className="text-xs text-slate-500">
              {role === "admin" ? "Full company access" : role === "staff" ? "Create/edit invoices" : "View only"}
            </span>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        {loading ? (
          <div className="p-12 animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-200 rounded"></div>)}</div>
        ) : (
          <div className="overflow-x-auto sticky-table">
            <table className="w-full">
              <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">User</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Company</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Role</th>
                  {isSuperAdmin && (
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-slate-600" data-testid="premium-column-header">Premium</th>
                  )}
                  {isSuperAdmin && (
                    <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-slate-600" data-testid="transport-column-header">Transport Invoice</th>
                  )}
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                    <td className="px-6 py-4 flex items-center gap-3">
                      {u.avatar ? (
                        <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <UserCircle size={32} weight="duotone" className="text-slate-400" />
                      )}
                      <span className="text-sm font-semibold text-slate-900">{u.name || "No Name"}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{u.company_name || "--"}</td>
                    <td className="px-6 py-4">
                      {u.role === "super_admin" ? (
                        <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider ${ROLE_COLORS.super_admin}`}>Super Admin</span>
                      ) : (
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          disabled={u.id === currentUser?.id}
                          data-testid={`role-select-${u.id}`}
                          className={`px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border-0 ${ROLE_COLORS[u.role]} ${u.id === currentUser?.id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <option value="admin">Admin</option>
                          <option value="staff">Staff</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      )}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4 text-center">
                        <label className="inline-flex items-center cursor-pointer gap-2">
                          <input
                            type="checkbox"
                            checked={!!u.premium_enabled}
                            onChange={(e) => handlePremiumToggle(u.id, e.target.checked)}
                            data-testid={`premium-toggle-${u.id}`}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-slate-200 peer-checked:bg-gradient-to-r peer-checked:from-amber-400 peer-checked:to-amber-600 rounded-full transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white" />
                          <span className={`text-[10px] font-bold tracking-wider ${u.premium_enabled ? "text-amber-700" : "text-slate-400"}`}>
                            {u.premium_enabled ? "ENABLED" : "OFF"}
                          </span>
                        </label>
                      </td>
                    )}
                    {isSuperAdmin && (
                      <td className="px-6 py-4 text-center">
                        <label className="inline-flex items-center cursor-pointer gap-2">
                          <input
                            type="checkbox"
                            checked={!!u.transport_invoice_enabled}
                            onChange={(e) => handleTransportToggle(u.id, e.target.checked)}
                            data-testid={`transport-toggle-${u.id}`}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-slate-200 peer-checked:bg-gradient-to-r peer-checked:from-sky-400 peer-checked:to-sky-600 rounded-full transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white" />
                          <span className={`text-[10px] font-bold tracking-wider ${u.transport_invoice_enabled ? "text-sky-700" : "text-slate-400"}`}>
                            {u.transport_invoice_enabled ? "ENABLED" : "OFF"}
                          </span>
                        </label>
                      </td>
                    )}
                    <td className="px-6 py-4 text-right">
                      {u.id !== currentUser?.id && u.role !== "super_admin" && (
                        <button onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`} className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200">
                          <Trash size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            {inviteError && <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm">{inviteError}</div>}
            <div>
              <Label>Full Name *</Label>
              <Input data-testid="invite-name-input" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="John Doe" required />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" data-testid="invite-email-input" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="user@company.com" required />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" data-testid="invite-password-input" value={inviteForm.password} onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })} placeholder="Set a password" required />
            </div>
            <div>
              <Label>Role *</Label>
              <select data-testid="invite-role-select" value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-invite-btn">Create User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
